import { gql } from '@apollo/client';
import type { Attachment } from '../types';

// Query per lista articoli
export const GET_CONTENTS = gql`
  query GetContents($limit: Int, $offset: Int, $status: ContentStatus, $visibility: Visibility) {
    contents(limit: $limit, offset: $offset, status: $status, visibility: $visibility) {
      id
      title
      slug
      excerpt
      status
      visibility
      featuredImage
      publishedAt
      createdAt
      author {
        id
        username
        profile {
          firstName
          lastName
          fullName
        }
      }
      category {
        id
        name
        slug
      }
      tags
      metadata {
        views
        likeCount
        shares
      }
      readingTime
    }
  }
`;

// Query per singolo articolo
export const GET_CONTENT_BY_SLUG = gql`
  query GetContentBySlug($slug: String!) {
    content(slug: $slug) {
      id
      title
      slug
      body
      excerpt
      status
      visibility
      featuredImage
      audioEnabled
      attachments {
        filename
        originalName
        url
        size
        mimeType
      }
      publishedAt
      createdAt
      updatedAt
      author {
        id
        username
        profile {
          firstName
          lastName
          fullName
          profilePicture
        }
      }
      category {
        id
        name
        slug
        description
      }
      tags
      metadata {
        views
        likeCount
        shares
      }
      seo {
        metaTitle
        metaDescription
        keywords
      }
      readingTime
      commentCount
    }
  }
`;

// Query per singolo articolo per ID (per editing)
export const GET_CONTENT_BY_ID = gql`
  query GetContentById($id: ID!) {
    content(id: $id) {
      id
      title
      slug
      body
      excerpt
      status
      visibility
      featuredImage
      audioEnabled
      attachments {
        filename
        originalName
        url
        size
        mimeType
      }
      publishedAt
      createdAt
      updatedAt
      author {
        id
        username
        profile {
          firstName
          lastName
          fullName
          profilePicture
        }
      }
      category {
        id
        name
        slug
        description
      }
      tags
      metadata {
        views
        likeCount
        shares
      }
      seo {
        metaTitle
        metaDescription
        keywords
      }
      readingTime
      commentCount
    }
  }
`;

// Query per contare articoli
export const GET_CONTENTS_COUNT = gql`
  query GetContentsCount($status: ContentStatus, $visibility: Visibility) {
    contentsCount(status: $status, visibility: $visibility)
  }
`;

// Mutation per creare articolo
export const CREATE_CONTENT = gql`
  mutation CreateContent($input: CreateContentInput!) {
    createContent(input: $input) {
      id
      title
      slug
      body
      excerpt
      status
      visibility
      featuredImage
      audioEnabled
      publishedAt
      createdAt
      updatedAt
      category {
        id
        name
        slug
        color
      }
      tags
      author {
        id
        username
        profile {
          firstName
          lastName
          fullName
        }
      }
      attachments {
        filename
        originalName
        url
        size
        mimeType
      }
      seo {
        metaTitle
        metaDescription
        keywords
      }
      metadata {
        views
        likeCount
      }
    }
  }
`;

// Mutation per aggiornare articolo
export const UPDATE_CONTENT = gql`
  mutation UpdateContent($id: ID!, $input: UpdateContentInput!) {
    updateContent(id: $id, input: $input) {
      id
      title
      slug
      body
      excerpt
      status
      visibility
      featuredImage
      audioEnabled
      publishedAt
      updatedAt
      createdAt
      category {
        id
        name
        slug
        color
      }
      tags
      author {
        id
        username
        profile {
          firstName
          lastName
          fullName
        }
      }
      attachments {
        filename
        originalName
        url
        size
        mimeType
      }
      seo {
        metaTitle
        metaDescription
        keywords
      }
      metadata {
        views
        likeCount
      }
    }
  }
`;

// Mutation per eliminare articolo
export const DELETE_CONTENT = gql`
  mutation DeleteContent($id: ID!) {
    deleteContent(id: $id)
  }
`;

// Mutation per cambiare stato articolo
export const UPDATE_CONTENT_STATUS = gql`
  mutation UpdateContentStatus($id: ID!, $status: ContentStatus!) {
    updateContent(id: $id, input: { status: $status }) {
      id
      status
      publishedAt
      updatedAt
    }
  }
`;

// Mutation per cambiare visibilità articolo
export const UPDATE_CONTENT_VISIBILITY = gql`
  mutation UpdateContentVisibility($id: ID!, $visibility: Visibility!) {
    updateContent(id: $id, input: { visibility: $visibility }) {
      id
      visibility
      updatedAt
    }
  }
`;

// Mutation per incrementare visualizzazioni
export const INCREMENT_VIEWS = gql`
  mutation IncrementViews($id: ID!) {
    incrementViews(id: $id) {
      id
      metadata {
        views
      }
    }
  }
`;

// Query per categorie (versione completa con tutti i campi)
export const GET_CATEGORIES = gql`
  query GetCategories {
    categories {
      id
      name
      slug
      description
      color
      icon
      order
      isActive
      metadata {
        contentCount
      }
      createdAt
      updatedAt
    }
  }
`;

// Query per gallerie
export const GET_GALLERIES = gql`
  query GetGalleries($limit: Int, $offset: Int, $status: String) {
    galleries(limit: $limit, offset: $offset, status: $status) {
      id
      title
      slug
      description
      status
      coverImage
      createdAt
      author {
        id
        username
        profile {
          firstName
          lastName
          fullName
        }
      }
      category
      tags
      metadata {
        views
        likeCount
        imageCount
      }
    }
  }
`;

// Query per statistiche dashboard admin
export const GET_DASHBOARD_STATS = gql`
  query GetDashboardStats($activityLimit: Int) {
    dashboardStats(activityLimit: $activityLimit) {
      totalArticles
      publishedArticles
      draftArticles
      archivedArticles
      totalUsers
      totalViews
      totalGalleries
      totalAssociates
      activeAssociates
      pendingMemberships
      totalCertifiedMembers
      activeCertifiedMembers
      pendingCertifiedApplications
      recentActivity {
        id
        type
        description
        timestamp
        user {
          id
          username
          profile {
            firstName
            lastName
            fullName
          }
        }
        relatedContent {
          id
          title
          slug
        }
      }
    }
  }
`;

// Query per singola galleria
export const GET_GALLERY_BY_SLUG = gql`
  query GetGalleryBySlug($slug: String!) {
    galleryBySlug(slug: $slug) {
      id
      title
      slug
      description
      status
      coverImage
      createdAt
      updatedAt
      author {
        id
        username
        profile {
          firstName
          lastName
          fullName
        }
      }
      category
      tags
      images {
        id
        filename
        originalName
        url
        thumbnailUrl
        alt
        caption
        metadata {
          width
          height
          size
          mimeType
        }
      }
      metadata {
        views
        likeCount
        imageCount
      }
    }
  }
`;

// Input Types (per TypeScript)
export interface CreateContentInput {
  title: string;
  slug: string; // ✅ Aggiunto campo slug obbligatorio
  body: string;
  excerpt?: string;
  categoryId?: string;
  tags?: string[];
  status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  visibility?: 'PUBLIC' | 'MEMBERS' | 'ADMIN';
  featuredImage?: string;
  audioEnabled?: boolean;
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
  };
  scheduledFor?: string;
}

export interface UpdateContentInput {
  title?: string;
  slug?: string; // ✅ Aggiunto campo slug opzionale per update
  body?: string;
  excerpt?: string;
  categoryId?: string;
  tags?: string[];
  status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  visibility?: 'PUBLIC' | 'MEMBERS' | 'ADMIN';
  featuredImage?: string;
  audioEnabled?: boolean;
  attachments?: Attachment[]; // ✅ Aggiunto campo attachments per update
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
  };
  scheduledFor?: string;
}

export interface GalleryInput {
  title: string;
  slug?: string;
  description?: string;
  category?: string;
  tags?: string[];
  status?: 'draft' | 'published' | 'archived';
  coverImage?: string;
  images?: {
    filename: string;
    originalName: string;
    url: string;
    thumbnailUrl?: string;
    alt?: string;
    caption?: string;
  }[];
}

// Response Types
export interface Content {
  id: string;
  title: string;
  slug: string;
  body?: string;
  excerpt?: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'; // Backend restituisce valori UPPERCASE
  visibility: 'PUBLIC' | 'MEMBERS' | 'ADMIN'; // Backend restituisce valori UPPERCASE
  featuredImage?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt?: string;
  author: {
    id: string;
    username: string;
    profile: {
      firstName?: string;
      lastName?: string;
      fullName?: string;
      profilePicture?: string;
    };
  };
  category?: {
    id: string;
    name: string;
    slug: string;
    description?: string;
  };
  tags: string[];
  metadata: {
    views: number;
    likeCount: number;
    shares: number;
  };
  seo?: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
  };
  readingTime: number;
  commentCount?: number;
}

export interface Gallery {
  id: string;
  title: string;
  slug: string;
  description?: string;
  status: 'draft' | 'published' | 'archived';
  coverImage?: string;
  createdAt: string;
  updatedAt?: string;
  author: {
    id: string;
    username: string;
    profile: {
      firstName?: string;
      lastName?: string;
      fullName?: string;
    };
  };
  category?: string;
  tags: string[];
  images?: {
    id: string;
    filename: string;
    originalName: string;
    url: string;
    thumbnailUrl?: string;
    alt?: string;
    caption?: string;
    metadata: {
      width: number;
      height: number;
      size: number;
      mimeType: string;
    };
  }[];
  metadata: {
    views: number;
    likeCount: number;
    imageCount: number;
  };
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  color?: string;
  icon?: string;
  order?: number;
  isActive?: boolean;
  metadata: {
    contentCount: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

// Category Mutations
export const CREATE_CATEGORY = gql`
  mutation CreateCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
      slug
      description
      color
      icon
      order
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_CATEGORY = gql`
  mutation UpdateCategory($id: ID!, $input: UpdateCategoryInput!) {
    updateCategory(id: $id, input: $input) {
      id
      name
      slug
      description
      color
      icon
      order
      isActive
      updatedAt
    }
  }
`;

export const DELETE_CATEGORY = gql`
  mutation DeleteCategory($id: ID!) {
    deleteCategory(id: $id)
  }
`;

// Settings Queries and Mutations
export const GET_SETTINGS = gql`
  query GetSettings {
    settings {
      id
      membershipNotificationEmail
      certifiedNetworkNotificationEmail
      contactFormNotificationEmail
      generalContactEmail
      generalContactPhone
      organizationName
      frontendUrl
      smtpHost
      smtpPort
      smtpSecure
      smtpUser
      smtpPassword
      smtpFromEmail
      smtpFromName
      turboSmtpConsumerKey
      turboSmtpConsumerSecret
      turboSmtpEmail
      turboSmtpPassword
      turboSmtpAuthToken
      useTurboSmtpApi
      turboSmtpSendEndpoint
      enableEmailAnalyticsSync
      emailsEnabled
      pushEnabled
      inAppNotificationsEnabled
      maintenanceMode
      maintenanceMessage
      mobileAppMinVersionIos
      mobileAppMinVersionAndroid
      mobileAppLatestVersion
      mobileAppFeatures { svincoloAtti voucher community surveys payments rappresentanti supporto changelog circolari chat dipendenti takata collabora facile eventi }
      mobileAppNascosti { svincoloAtti voucher community surveys supporto changelog takata collabora facile eventi circolari }
      audioNativeEnabled
      audioNativePublicUserId
      rcWelcomeVideoEnabled
      rcWelcomeVideoUrl
      rcWelcomeVideoTitle
      legalEntityName
      legalEntityAddress
      legalEntityPhone
      legalEntityFax
      legalEntityEmail
      legalEntityPec
      legalRepresentative
      vatNumber
      fiscalCode
      dpoName
      dpoEmail
      dpoPhone
      cookieBannerEnabled
      openaiModel
      verbaliModel
      verbaliTrascrizioneModel
      verbaliTettoMinutiAudio
      verbaliTettoGenerazioni
      verbali { socialAttivo prezzoAudioMinuto prezzoTokenInputMilione prezzoTokenOutputMilione }
      verbaliPostpickrConfigurato
      lastModifiedBy {
        id
        username
        profile {
          fullName
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_MAINTENANCE_STATUS = gql`
  query GetMaintenanceStatus {
    maintenanceStatus {
      isActive
      message
    }
  }
`;

export const UPDATE_SETTINGS = gql`
  mutation UpdateSettings($input: UpdateSettingsInput!) {
    updateSettings(input: $input) {
      id
      membershipNotificationEmail
      certifiedNetworkNotificationEmail
      contactFormNotificationEmail
      generalContactEmail
      generalContactPhone
      organizationName
      frontendUrl
      smtpHost
      smtpPort
      smtpSecure
      smtpUser
      smtpPassword
      smtpFromEmail
      smtpFromName
      turboSmtpConsumerKey
      turboSmtpConsumerSecret
      turboSmtpEmail
      turboSmtpPassword
      turboSmtpAuthToken
      useTurboSmtpApi
      turboSmtpSendEndpoint
      enableEmailAnalyticsSync
      emailsEnabled
      pushEnabled
      inAppNotificationsEnabled
      maintenanceMode
      maintenanceMessage
      mobileAppMinVersionIos
      mobileAppMinVersionAndroid
      mobileAppLatestVersion
      mobileAppFeatures { svincoloAtti voucher community surveys payments rappresentanti supporto changelog circolari chat dipendenti takata collabora facile eventi }
      mobileAppNascosti { svincoloAtti voucher community surveys supporto changelog takata collabora facile eventi circolari }
      audioNativeEnabled
      audioNativePublicUserId
      rcWelcomeVideoEnabled
      rcWelcomeVideoUrl
      rcWelcomeVideoTitle
      legalEntityName
      legalEntityAddress
      legalEntityPhone
      legalEntityFax
      legalEntityEmail
      legalEntityPec
      legalRepresentative
      vatNumber
      fiscalCode
      dpoName
      dpoEmail
      dpoPhone
      cookieBannerEnabled
      openaiModel
      verbaliModel
      verbaliTrascrizioneModel
      verbaliTettoMinutiAudio
      verbaliTettoGenerazioni
      verbali { socialAttivo prezzoAudioMinuto prezzoTokenInputMilione prezzoTokenOutputMilione }
      verbaliPostpickrConfigurato
      lastModifiedBy {
        id
        username
        profile {
          fullName
        }
      }
      updatedAt
    }
  }
`;

export interface Settings {
  id: string;
  membershipNotificationEmail: string;
  certifiedNetworkNotificationEmail: string;
  contactFormNotificationEmail: string;
  generalContactEmail: string;
  generalContactPhone: string;
  organizationName: string;
  frontendUrl: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPassword: string;
  smtpFromEmail: string;
  smtpFromName: string;
  turboSmtpConsumerKey: string;
  turboSmtpConsumerSecret: string;
  turboSmtpEmail: string;
  turboSmtpPassword: string;
  turboSmtpAuthToken: string;
  useTurboSmtpApi: boolean;
  turboSmtpSendEndpoint: string;
  enableEmailAnalyticsSync: boolean;
  emailsEnabled: boolean;
  pushEnabled: boolean;
  inAppNotificationsEnabled: boolean;
  maintenanceMode: boolean;
  maintenanceMessage: string;
  mobileAppMinVersionIos: string;
  mobileAppMinVersionAndroid: string;
  mobileAppLatestVersion: string;
  mobileAppFeatures: { svincoloAtti: boolean; voucher: boolean; community: boolean; surveys: boolean; payments: boolean; rappresentanti: boolean; supporto: boolean; changelog: boolean; circolari: boolean; chat: boolean; dipendenti: boolean; takata: boolean; collabora: boolean; facile: boolean; eventi: boolean };
  mobileAppNascosti: { svincoloAtti: boolean; voucher: boolean; community: boolean; surveys: boolean; supporto: boolean; changelog: boolean; takata: boolean; collabora: boolean; facile: boolean; eventi: boolean; circolari: boolean };
  audioNativeEnabled: boolean;
  audioNativePublicUserId: string;
  rcWelcomeVideoEnabled: boolean;
  rcWelcomeVideoUrl: string;
  rcWelcomeVideoTitle: string;
  legalEntityName: string;
  legalEntityAddress: string;
  legalEntityPhone: string;
  legalEntityFax: string;
  legalEntityEmail: string;
  legalEntityPec: string;
  legalRepresentative: string;
  vatNumber: string;
  fiscalCode: string;
  dpoName: string;
  dpoEmail: string;
  dpoPhone: string;
  cookieBannerEnabled: boolean;
  openaiModel: string;
  verbaliModel: string;
  verbaliTrascrizioneModel: string;
  verbaliTettoMinutiAudio: number;
  verbaliTettoGenerazioni: number;
  verbali: {
    socialAttivo: boolean;
    /** € per minuto di trascrizione (costo stimato dei consumi). */
    prezzoAudioMinuto: number;
    /** € per 1 milione di token in ingresso. */
    prezzoTokenInputMilione: number;
    /** € per 1 milione di token in uscita. */
    prezzoTokenOutputMilione: number;
  };
  /** POSTPICKR_MCP_URL presente sul server (l'URL non arriva mai al sito). */
  verbaliPostpickrConfigurato: boolean;
  lastModifiedBy?: {
    id: string;
    username: string;
    profile: {
      fullName: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface UpdateSettingsInput {
  membershipNotificationEmail?: string;
  certifiedNetworkNotificationEmail?: string;
  contactFormNotificationEmail?: string;
  generalContactEmail?: string;
  generalContactPhone?: string;
  organizationName?: string;
  frontendUrl?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpSecure?: boolean;
  smtpUser?: string;
  smtpPassword?: string;
  smtpFromEmail?: string;
  smtpFromName?: string;
  turboSmtpConsumerKey?: string;
  turboSmtpConsumerSecret?: string;
  turboSmtpEmail?: string;
  turboSmtpPassword?: string;
  turboSmtpAuthToken?: string;
  useTurboSmtpApi?: boolean;
  turboSmtpSendEndpoint?: string;
  enableEmailAnalyticsSync?: boolean;
  emailsEnabled?: boolean;
  pushEnabled?: boolean;
  inAppNotificationsEnabled?: boolean;
  maintenanceMode?: boolean;
  maintenanceMessage?: string;
  mobileAppMinVersionIos?: string;
  mobileAppMinVersionAndroid?: string;
  mobileAppLatestVersion?: string;
  mobileAppFeatures?: { svincoloAtti?: boolean; voucher?: boolean; community?: boolean; surveys?: boolean; payments?: boolean; rappresentanti?: boolean; supporto?: boolean; changelog?: boolean; circolari?: boolean; chat?: boolean; dipendenti?: boolean; takata?: boolean; collabora?: boolean; facile?: boolean; eventi?: boolean };
  mobileAppNascosti?: { svincoloAtti?: boolean; voucher?: boolean; community?: boolean; surveys?: boolean; supporto?: boolean; changelog?: boolean; takata?: boolean; collabora?: boolean; facile?: boolean; eventi?: boolean; circolari?: boolean };
  audioNativeEnabled?: boolean;
  audioNativePublicUserId?: string;
  rcWelcomeVideoEnabled?: boolean;
  rcWelcomeVideoUrl?: string;
  rcWelcomeVideoTitle?: string;
  legalEntityName?: string;
  legalEntityAddress?: string;
  legalEntityPhone?: string;
  legalEntityFax?: string;
  legalEntityEmail?: string;
  legalEntityPec?: string;
  legalRepresentative?: string;
  vatNumber?: string;
  fiscalCode?: string;
  dpoName?: string;
  dpoEmail?: string;
  dpoPhone?: string;
  cookieBannerEnabled?: boolean;
  openaiModel?: string;
  verbaliModel?: string;
  verbaliTrascrizioneModel?: string;
  verbaliTettoMinutiAudio?: number;
  verbaliTettoGenerazioni?: number;
  verbali?: { socialAttivo?: boolean; prezzoAudioMinuto?: number; prezzoTokenInputMilione?: number; prezzoTokenOutputMilione?: number };
}

export interface MaintenanceStatus {
  isActive: boolean;
  message: string;
}

// Audio Native Settings (public)
export const GET_AUDIO_NATIVE_SETTINGS = gql`
  query GetAudioNativeSettings {
    audioNativeSettings {
      enabled
      publicUserId
    }
  }
`;

export interface AudioNativeSettings {
  enabled: boolean;
  publicUserId: string;
}

// Legal Settings (public)
export const GET_LEGAL_SETTINGS = gql`
  query GetLegalSettings {
    legalSettings {
      legalEntityName
      legalEntityAddress
      legalEntityPhone
      legalEntityFax
      legalEntityEmail
      legalEntityPec
      legalRepresentative
      vatNumber
      fiscalCode
      dpoName
      dpoEmail
      dpoPhone
      cookieBannerEnabled
    }
  }
`;

export interface LegalSettings {
  legalEntityName: string;
  legalEntityAddress: string;
  legalEntityPhone: string;
  legalEntityFax: string;
  legalEntityEmail: string;
  legalEntityPec: string;
  legalRepresentative: string;
  vatNumber: string;
  fiscalCode: string;
  dpoName: string;
  dpoEmail: string;
  dpoPhone: string;
  cookieBannerEnabled: boolean;
}
